import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDate, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnDanger, BtnGhost,
  Modal, Field, Input, Select, Textarea, Empty, StatusBadge,
} from "@/components/primitives";
import LineItemsEditor from "@/components/LineItemsEditor";
import { toast } from "sonner";
import { Plus, DownloadSimple, Check, X, Package, FileText, Eye } from "@phosphor-icons/react";
import SmartInput from "@/components/SmartInput";
import ProjectSelect from "@/components/ProjectSelect";

export default function PurchaseOrders() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(null);
  const [skus, setSkus] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [costCategories, setCostCategories] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [costCenter, setCostCenter] = useState("");
  const [costCategory, setCostCategory] = useState("");
  const [projectCode, setProjectCode] = useState("");
  const [justification, setJustification] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [tax, setTax] = useState(0);
  const canApprove = can(user.role, "manager");
  const canCreate = can(user.role, "staff");

  async function load() {
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const { data } = await api.get(`/purchase-orders${params}`);
      setRows(data);
    } catch (e) { toast.error(formatError(e)); }
  }
  async function loadSkus() {
    try { const { data } = await api.get("/inventory"); setSkus(data); } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
  }
  async function loadSuppliers() {
    try { const { data } = await api.get("/suppliers?active_only=true"); setSuppliers(data); } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
  }
  async function loadLookups() {
    try {
      const [cc, ca] = await Promise.all([
        api.get("/lookups/cost_center"), api.get("/lookups/cost_category"),
      ]);
      setCostCenters(cc.data || []); setCostCategories(ca.data || []);
    } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
  }
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { loadSkus(); loadSuppliers(); loadLookups(); }, []);

  function openCreate() {
    setSupplier(""); setJustification(""); setNotes(""); setProjectCode("");
    setCostCenter(""); setCostCategory("");
    setItems([{ description: "", sku: "", quantity: 1, unit_price: 0 }]);
    setTax(0); setCreateOpen(true);
  }
  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/purchase-orders", {
        supplier,
        items: items.map((i) => ({
          description: i.description, sku: i.sku || null,
          quantity: Number(i.quantity), unit_price: Number(i.unit_price),
        })),
        tax_rate: Number(tax), justification, notes,
        project_code: projectCode || null,
        cost_center: costCenter || null,
        cost_category: costCategory || null,
      });
      toast.success("PO submitted for approval");
      setCreateOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function act(po, action, note) {
    try {
      await api.post(`/purchase-orders/${po.id}/${action}`, { note: note || null });
      toast.success(`PO ${action}ed`);
      setViewOpen(null); load();
    } catch (e) { toast.error(formatError(e)); }
  }
  async function receive(po) {
    if (!window.confirm(`Mark PO ${po.number} as received and restock inventory?`)) return;
    try {
      await api.post(`/purchase-orders/${po.id}/receive`);
      toast.success("Received & inventory updated");
      setViewOpen(null); load();
    } catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="po-page">
      <PageHeader
        overline="Module / Procurement"
        title="Purchase Orders"
        subtitle="Staff submit → Managers approve → Receive to auto-restock."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `pos-${Date.now()}.csv`)} data-testid="po-export">
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> CSV
            </BtnSecondary>
            {canCreate && (
              <BtnPrimary onClick={openCreate} data-testid="po-create">
                <Plus size={14} weight="bold" className="inline mr-1.5" /> New PO
              </BtnPrimary>
            )}
          </>
        }
      />

      <div className="phizo-card p-3 mb-4 flex flex-wrap gap-2">
        {["all", "pending", "approved", "rejected", "received"].map((s) => (
          <button
            key={s}
            data-testid={`po-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors border ${
              filter === s
                ? "bg-[#09090b] text-white border-[#09090b]"
                : "border-[#e4e4e7] text-[#52525b] hover:bg-[#f4f4f5]"
            }`}
          >{s}</button>
        ))}
      </div>

      <div className="phizo-card">
        {rows.length === 0 ? <div className="p-5"><Empty label="No purchase orders." /></div> : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th>Number</th><th>Supplier</th><th>Requested By</th>
                  <th>Created</th><th className="text-right">Total</th>
                  <th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((po) => (
                  <tr
                    key={po.id}
                    data-testid={`po-row-${po.number}`}
                    onClick={() => nav(`/purchase-orders/${po.id}`)}
                    className="cursor-pointer hover:bg-[#f4f4f5]"
                  >
                    <td className="mono font-semibold">{po.number}</td>
                    <td>{po.supplier}</td>
                    <td className="text-sm text-[#52525b]">{po.requested_by_name}</td>
                    <td className="text-sm text-[#52525b]">{fmtDate(po.created_at)}</td>
                    <td className="text-right mono font-semibold">{fmtZMW(po.total)}</td>
                    <td><StatusBadge status={po.status} /></td>
                    <td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <BtnGhost onClick={() => setViewOpen(po)} data-testid={`po-view-${po.number}`}>
                        <Eye size={12} weight="bold" /> View
                      </BtnGhost>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)}
        title="New Purchase Order" wide testId="po-create-modal">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Supplier" hint="Pick from saved suppliers or type a new one">
            <SmartInput
              required value={supplier}
              testId="po-supplier"
              onChange={setSupplier}
              options={suppliers.map((s) => ({ value: s.name, hint: s.code }))}
              placeholder="Start typing supplier name…"
            />
          </Field>
          <Field label="Project / Cost-centre (optional)">
            <ProjectSelect value={projectCode} onChange={setProjectCode} testId="po-project" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Cost Center" hint="From master list or type a new one">
              <SmartInput
                testId="po-cost-center"
                value={costCenter}
                onChange={setCostCenter}
                options={costCenters.map((c) => ({ value: c.name, hint: c.code || "" }))}
                placeholder="e.g. Crushing"
              />
            </Field>
            <Field label="Cost Category" hint="From master list or type a new one">
              <SmartInput
                testId="po-cost-category"
                value={costCategory}
                onChange={setCostCategory}
                options={costCategories.map((c) => ({ value: c.name, hint: c.code || "" }))}
                placeholder="e.g. Maintenance"
              />
            </Field>
          </div>
          <LineItemsEditor
            items={items} setItems={setItems}
            taxRate={tax} setTaxRate={setTax}
            skuOptions={skus}
          />
          <Field label="Justification (why this is needed)">
            <Textarea data-testid="po-justification"
              value={justification}
              onChange={(e) => setJustification(e.target.value)} />
          </Field>
          <Field label="Notes">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button" onClick={() => setCreateOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary type="submit" data-testid="po-submit">Submit for Approval</BtnPrimary>
          </div>
        </form>
      </Modal>

      {/* View modal */}
      <Modal open={!!viewOpen} onClose={() => setViewOpen(null)}
        title={viewOpen ? `PO ${viewOpen.number}` : ""} wide testId="po-view-modal">
        {viewOpen && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="phizo-label">Supplier</div><div className="font-semibold">{viewOpen.supplier}</div></div>
              <div><div className="phizo-label">Status</div><StatusBadge status={viewOpen.status} /></div>
              <div><div className="phizo-label">Requested By</div><div>{viewOpen.requested_by_name}</div></div>
              <div><div className="phizo-label">Created</div><div>{fmtDate(viewOpen.created_at)}</div></div>
              {viewOpen.approver_name && (
                <div><div className="phizo-label">Approver</div><div>{viewOpen.approver_name}</div></div>
              )}
              {viewOpen.cost_center && (
                <div><div className="phizo-label">Cost Center</div><div>{viewOpen.cost_center}</div></div>
              )}
              {viewOpen.cost_category && (
                <div><div className="phizo-label">Cost Category</div><div>{viewOpen.cost_category}</div></div>
              )}
            </div>

            {viewOpen.justification && (
              <div className="phizo-surface p-3 text-sm">
                <div className="phizo-label mb-1">Justification</div>
                {viewOpen.justification}
              </div>
            )}

            <div className="border border-[#e4e4e7]">
              <table className="w-full text-sm">
                <thead className="bg-[#f4f4f5] border-b border-[#e4e4e7]">
                  <tr>
                    <th className="text-left px-3 py-2 phizo-label">Description</th>
                    <th className="text-left px-3 py-2 phizo-label">SKU</th>
                    <th className="text-right px-3 py-2 phizo-label">Qty</th>
                    <th className="text-right px-3 py-2 phizo-label">Unit</th>
                    <th className="text-right px-3 py-2 phizo-label">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {viewOpen.items.map((it, idx) => (
                    <tr key={idx} className="border-b border-[#e4e4e7]">
                      <td className="p-3">{it.description}</td>
                      <td className="p-3 mono text-xs">{it.sku || "—"}</td>
                      <td className="p-3 text-right mono">{it.quantity}</td>
                      <td className="p-3 text-right mono">{fmtZMW(it.unit_price)}</td>
                      <td className="p-3 text-right mono font-semibold">{fmtZMW(it.quantity * it.unit_price)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={4} className="p-3 text-right phizo-label">Subtotal</td><td className="p-3 text-right mono">{fmtZMW(viewOpen.subtotal)}</td></tr>
                  <tr><td colSpan={4} className="p-3 text-right phizo-label">Tax ({viewOpen.tax_rate}%)</td><td className="p-3 text-right mono">{fmtZMW(viewOpen.tax)}</td></tr>
                  <tr className="bg-[#09090b] text-white"><td colSpan={4} className="p-3 text-right phizo-label text-white">Total</td><td className="p-3 text-right font-heading font-black text-lg">{fmtZMW(viewOpen.total)}</td></tr>
                </tfoot>
              </table>
            </div>

            <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
              {viewOpen.status === "pending" && canApprove && (
                <>
                  <BtnDanger onClick={() => act(viewOpen, "reject")} data-testid="po-reject">
                    <X size={14} weight="bold" className="inline mr-1" /> Reject
                  </BtnDanger>
                  <BtnPrimary onClick={() => act(viewOpen, "approve")} data-testid="po-approve">
                    <Check size={14} weight="bold" className="inline mr-1" /> Approve
                  </BtnPrimary>
                </>
              )}
              {viewOpen.status === "approved" && canCreate && (
                <BtnPrimary onClick={() => receive(viewOpen)} data-testid="po-receive">
                  <Package size={14} weight="bold" className="inline mr-1" /> Mark Received
                </BtnPrimary>
              )}
              <BtnSecondary onClick={() => setViewOpen(null)}>Close</BtnSecondary>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
